import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useApiFetcher } from "../ApiProvider";
import { QUERY_KEYS } from "../queryKeys";
import type { UserSettings } from "../types";
import type { UseQueryOptions } from "@tanstack/react-query";
type OverrideOptions = Omit<Partial<UseQueryOptions<UserSettings>>, "queryKey" | "queryFn">;

/**
 * Fetches the current user's settings.
 *
 * Caching defaults are intentionally loose so each app can override
 * via `options`. For example, web may set `staleTime: 0` with
 * `refetchOnMount: "always"`, while desktop may use a 5-minute
 * polling interval.
 *
 * @param options - Override any useQuery option (staleTime, gcTime, refetchInterval, etc.)
 */
export function useUserSettings(options) {
  const $ = _c(5);
  const apiFetcher = useApiFetcher();
  let t0;
  if ($[0] !== apiFetcher) {
    t0 = () => apiFetcher("/api/v1/user/settings");
    $[0] = apiFetcher;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      queryKey: QUERY_KEYS.USER_SETTINGS,
      queryFn: t0,
      staleTime: 300000,
      gcTime: 600000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useQuery(t1);
}